import { useEffect, useState } from "react";
import {
  adminGetEvents, adminUpsertEvent, adminEventStatus, adminDeleteEvent,
  adminCheckActivation, adminGetEventRegistrations,
} from "@/lib/api";
import { Trash2, Plus, X, Users } from "lucide-react";

const EMPTY = {
  slug: "", title: "", kind: "workshop", format: "", event_time: "", location: "",
  event_date: "", activation_date: "", seats_total: 10, activation_threshold: 6,
  description: "", who_for: "", what_leave: "", why_now: "", tickets: [],
  status: "draft", order: 99,
};
const STATUSES = ["draft", "open", "closed", "archived"];

export default function AdminEvents() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [msg, setMsg] = useState("");
  const [regs, setRegs] = useState(null); // { slug, data }
  const load = () => adminGetEvents().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    if (!form.slug.trim() || !form.title.trim()) { setMsg("Slug and title are required."); return; }
    const tickets = (form.tickets || [])
      .filter((t) => (t.name || "").trim())
      .map((t) => ({ ...t, price: Number(t.price) }));
    if (tickets.some((t) => !(t.price > 0))) { setMsg("Every ticket needs a price greater than zero."); return; }
    try {
      await adminUpsertEvent({
        ...form, tickets,
        seats_total: Number(form.seats_total),
        activation_threshold: Number(form.activation_threshold),
        order: Number(form.order),
      });
      setForm(EMPTY); load();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Could not save event.");
    }
  };
  const setStatus = async (slug, status) => { await adminEventStatus(slug, status); load(); };
  const remove = async (slug) => { await adminDeleteEvent(slug); load(); };
  const check = async (slug) => { const r = await adminCheckActivation(slug); setMsg(`${slug}: ${r.action || "checked"} (${r.count}/${r.threshold})`); load(); };
  const edit = (ev) => setForm({ ...EMPTY, ...ev, tickets: ev.tickets || [] });
  const viewRegs = async (slug) => {
    const data = await adminGetEventRegistrations(slug);
    setRegs({ slug, data });
  };

  // ---- ticket editor helpers ----
  const addTicket = () => setForm((f) => ({ ...f, tickets: [...(f.tickets || []), { name: "", price: "", description: "" }] }));
  const updateTicket = (i, key, val) => setForm((f) => {
    const t = [...(f.tickets || [])];
    t[i] = { ...t[i], [key]: val };
    return { ...f, tickets: t };
  });
  const removeTicket = (i) => setForm((f) => ({ ...f, tickets: (f.tickets || []).filter((_, idx) => idx !== i) }));

  return (
    <div data-testid="admin-events">
      <p className="cv-kicker">Events</p>
      <h1 className="cv-display text-3xl mt-4">Create and run the rooms.</h1>

      {/* Form */}
      <form data-testid="admin-event-form" onSubmit={submit} className="mt-8 border border-[rgba(28,27,25,0.22)] rounded-[16px] p-6 max-w-2xl space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Slug"><input data-testid="admin-event-slug" className="cv-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></Field>
          <Field label="Title"><input data-testid="admin-event-title" className="cv-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Kind (sets the fallback price)">
            <select data-testid="admin-event-kind" className="cv-input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              <option value="workshop">Workshop ($1,500)</option>
              <option value="dinner">Dinner ($350)</option>
              <option value="retreat">Retreat ($200 deposit)</option>
            </select>
          </Field>
          <Field label="Format"><input className="cv-input" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} /></Field>
          <Field label="Event date (YYYY-MM-DD)"><input data-testid="admin-event-date" className="cv-input" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} /></Field>
          <Field label="Time (e.g. 6:30 PM ET)"><input data-testid="admin-event-time" className="cv-input" value={form.event_time} onChange={(e) => setForm({ ...form, event_time: e.target.value })} /></Field>
          <Field label="Location"><input data-testid="admin-event-location" className="cv-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
          <Field label="Activation date (YYYY-MM-DD)"><input className="cv-input" value={form.activation_date} onChange={(e) => setForm({ ...form, activation_date: e.target.value })} /></Field>
          <Field label="Seats total"><input type="number" className="cv-input" value={form.seats_total} onChange={(e) => setForm({ ...form, seats_total: e.target.value })} /></Field>
          <Field label="Activation threshold"><input type="number" className="cv-input" value={form.activation_threshold} onChange={(e) => setForm({ ...form, activation_threshold: e.target.value })} /></Field>
          <Field label="Status">
            <select className="cv-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Order"><input type="number" className="cv-input" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></Field>
        </div>
        <Field label="Description"><textarea rows={2} className="cv-input resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
        <Field label="Who this is for"><textarea rows={2} className="cv-input resize-none" value={form.who_for} onChange={(e) => setForm({ ...form, who_for: e.target.value })} /></Field>
        <Field label="What you will leave with"><textarea rows={2} className="cv-input resize-none" value={form.what_leave} onChange={(e) => setForm({ ...form, what_leave: e.target.value })} /></Field>
        <Field label="Why now"><textarea rows={2} className="cv-input resize-none" value={form.why_now} onChange={(e) => setForm({ ...form, why_now: e.target.value })} /></Field>

        {/* Ticket editor */}
        <div data-testid="admin-ticket-editor" className="border-t border-[rgba(28,27,25,0.14)] pt-4">
          <div className="flex items-center justify-between">
            <span className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60">Ticket types (leave empty to use the kind price)</span>
            <button type="button" data-testid="admin-ticket-add" onClick={addTicket} className="cv-link inline-flex items-center gap-1 text-sm"><Plus size={13} /> Add ticket</button>
          </div>
          <div className="mt-3 space-y-3">
            {(form.tickets || []).length === 0 && (
              <p className="cv-body text-xs text-[#9A938A]">No custom tickets. The {form.kind} fallback price will apply.</p>
            )}
            {(form.tickets || []).map((t, i) => (
              <div key={i} data-testid="admin-ticket-row" className="grid grid-cols-1 sm:grid-cols-[1.2fr_0.6fr_1.4fr_auto] gap-2 items-start">
                <input className="cv-input" placeholder="Name (e.g. Operator seat)" value={t.name} onChange={(e) => updateTicket(i, "name", e.target.value)} data-testid="admin-ticket-name" />
                <input className="cv-input" type="number" placeholder="Price" value={t.price} onChange={(e) => updateTicket(i, "price", e.target.value)} data-testid="admin-ticket-price" />
                <input className="cv-input" placeholder="Short description (optional)" value={t.description || ""} onChange={(e) => updateTicket(i, "description", e.target.value)} />
                <button type="button" onClick={() => removeTicket(i)} className="text-[#A65A43] mt-2.5" data-testid="admin-ticket-remove"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>

        {msg && <p data-testid="admin-event-msg" className="cv-body text-sm text-[#1F1B16]/80 border-l-2 border-[#515E4F] pl-3">{msg}</p>}
        <button data-testid="admin-event-submit" className="cv-btn-primary inline-flex items-center gap-2"><Plus size={14} /> Save event</button>
      </form>

      {/* List */}
      <div className="mt-8 space-y-3 max-w-2xl">
        {items.map((ev) => (
          <div key={ev.slug} data-testid="admin-event-row" className="border border-[rgba(28,27,25,0.14)] rounded-[14px] p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="cv-kicker">{ev.kind} · {ev.status}</span>
                <h3 className="cv-h2 text-base mt-1">{ev.title}</h3>
                <p className="cv-body text-xs text-[#9A938A] mt-1">{ev.registrations} registered / threshold {ev.activation_threshold} · {ev.event_date}</p>
                {ev.tickets?.length > 0 && (
                  <p className="cv-body text-xs text-[#1F1B16]/60 mt-1">{ev.tickets.map((t) => `${t.name} ($${Number(t.price).toLocaleString()})`).join(" · ")}</p>
                )}
              </div>
              <button className="text-[#A65A43]" data-testid="admin-event-delete" onClick={() => remove(ev.slug)}><Trash2 size={14} /></button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <button className="cv-link" onClick={() => edit(ev)}>Edit</button>
              <button data-testid="admin-event-registrations" className="cv-link inline-flex items-center gap-1 text-[#515E4F]" onClick={() => viewRegs(ev.slug)}><Users size={13} /> Registrations</button>
              {STATUSES.map((s) => (
                <button key={s} data-testid={`admin-event-status-${s}`} onClick={() => setStatus(ev.slug, s)} className={`cv-sans uppercase tracking-[0.1em] text-[0.66rem] px-2.5 py-1 rounded-full border ${ev.status === s ? "border-[#A6633C] text-[#A6633C]" : "border-[rgba(28,27,25,0.2)] text-[#1F1B16]/60"}`}>{s}</button>
              ))}
              <button data-testid="admin-event-check" className="cv-link text-[#515E4F]" onClick={() => check(ev.slug)}>Check activation</button>
            </div>
          </div>
        ))}
      </div>

      {/* Registrations drawer */}
      {regs && (
        <div className="fixed inset-0 z-[200] flex justify-end" data-testid="admin-regs-overlay">
          <div className="absolute inset-0 bg-[#1F1B16]/40 backdrop-blur-sm" onClick={() => setRegs(null)} />
          <div className="relative w-full max-w-lg bg-[#F6F2EC] h-full overflow-y-auto p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="cv-kicker">Registrations</p>
                <h2 className="cv-display text-2xl mt-1">{regs.slug}</h2>
              </div>
              <button onClick={() => setRegs(null)} data-testid="admin-regs-close" className="text-[#1F1B16]/60"><X size={20} /></button>
            </div>
            <div className="flex gap-4 mt-5 cv-body text-sm">
              <Stat n={regs.data.counts.registered} label="Registered" />
              <Stat n={regs.data.counts.abandoned} label="Abandoned" />
              <Stat n={regs.data.counts.in_progress} label="In progress" />
            </div>

            <RegGroup title="Registered (paid)" list={regs.data.registered} accent="#515E4F" />
            <RegGroup title="Abandoned (started, did not pay)" list={regs.data.abandoned} accent="#A65A43" testid="admin-regs-abandoned" />
            <RegGroup title="In progress" list={regs.data.in_progress} accent="#A6633C" />
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <p className="cv-display text-2xl text-[#1F1B16]">{n}</p>
      <p className="cv-sans uppercase tracking-[0.1em] text-[0.62rem] text-[#1F1B16]/55">{label}</p>
    </div>
  );
}

function RegGroup({ title, list, accent, testid }) {
  return (
    <div className="mt-7" data-testid={testid}>
      <p className="cv-sans uppercase tracking-[0.12em] text-[0.66rem]" style={{ color: accent }}>{title}</p>
      <div className="mt-3 space-y-3">
        {(!list || list.length === 0) && <p className="cv-body text-xs text-[#9A938A]">None yet.</p>}
        {(list || []).map((l) => (
          <div key={l.id} className="border border-[rgba(28,27,25,0.14)] rounded-[12px] p-4">
            <div className="flex items-center justify-between gap-2">
              <p className="cv-h2 text-sm">{l.name || "Unnamed"}</p>
              <span className="cv-serif text-sm text-[#1F1B16]/70">{l.ticket_name}{l.amount != null ? ` · $${Number(l.amount).toLocaleString()}` : ""}</span>
            </div>
            <p className="cv-body text-xs text-[#1F1B16]/75 mt-1">{l.email}{l.phone ? ` · ${l.phone}` : ""}{l.company ? ` · ${l.company}` : ""}</p>
            {l.note && <p className="cv-body text-xs text-[#1F1B16]/60 mt-2 italic">“{l.note}”</p>}
            <p className="cv-body text-[0.65rem] text-[#9A938A] mt-2">Started {l.created_at?.slice(0, 16).replace("T", " ")}{l.abandoned_at ? ` · abandoned ${l.abandoned_at.slice(0, 16).replace("T", " ")}` : ""}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
